import { Button, Grid, Typography, Box, Container } from "@mui/material";
import { useLocation } from "react-router-dom";
import Solutions from "./solutions";
import { useEffect } from "react";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import Footer from "../Footer";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { ThemeProvider } from "@mui/material/styles";
import theme from "../style/theme";
import BlogCards from "./blog";

const financialSolutions = {
  financialServices: {
    heading: "Empower Finance to Lead Cloud Strategy",
    features: [
      "Maintain SLA and compliance while optimizing spend",
      "Auto-scale batch jobs like reporting or analytics",
      "Avoid vendor lock-in through flexible instance policies",
      "Meet internal audit requirements with full visibility",
    ],
    subHead: "Set budgets and receive alerts before they’re exceeded",
    para: "Understand exactly where your money is going — and where it’s being wasted.",
    image: "/images/finance.png",
    href: "/services/finance",
  },
  saas: {
    heading: "Purpose-Built Cloud Efficiency for Fast-Moving SaaS Teams",
    features: [
      "Share infrastructure without overpaying",
      "Assign cost by tenant using tagging and billing groups",
      "Scale per-user features via spot/on-demand mix",
      "Gain real-time insights across multiple environments",
    ],
    subHead: "Multi-tenant optimization, simplified.",
    para: "Our platform automates AWS cost optimization for SaaS and Independent Software Vendor (ISV) organizations, so you can focus on your product, not infrastructure waste.",
    image: "/images/saas.png",
    href: "/services/saas-program",
  },
  healthCare: {
    heading: "AWS Cost Optimization for Health and Life Sciences Organizations",
    features: [
      "Ensure HIPAA/GDPR compliance with cloud usage policies",
      "Optimize EMR/EHR workloads across compute tiers",
      "Track usage by department or research project",
      "Maintain security while reducing idle runtime",
    ],
    subHead: "Secure, scalable, and cost-efficient.",
    para: "",
    image: "/images/cloud.png",
    href: "/services/health-Care",
  },
  media: {
    features: [
      "Scale up instantly for traffic spikes during events",
      "Use spot fleets for rendering, encoding, or batch processing",
      "Ensure playback quality by isolating latency-critical nodes",
      "Visualize per-project cost and usage",
    ],
    heading: "AWS Cost Optimization for Media and Entertainment Organizations",
    subHead: "Burst ready. Budget friendly.",
    para: "",
    image: "/images/media.png",
    href: "/services/media-entertainment",
  },
};

const FinancialSolutionSection = ({ id, data, index }) => (
  <Box
    id={id}
    sx={{
      py: 8,
      bgcolor: index % 2 === 0 ? "background.default" : "background.paper",
    }}
  >
    <Container maxWidth="lg">
      <Grid
        container
        spacing={8}
        alignItems="center"
        direction={index % 2 === 0 ? "row" : "row-reverse"}
      >
        <Grid item xs={12} md={6} width={{ lg: "60%" }}>
          <Typography
            variant="h3"
            sx={{ fontWeight: "bold", mb: 2, color: "primary.main" }}
          >
            {data.heading}
          </Typography>
          <Typography variant="h5" color="text.secondary" sx={{ mb: 3 }}>
            {data.subHead}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {data.para}
          </Typography>
          {data.features.map((feature, idx) => (
            <Box
              key={idx}
              sx={{ display: "flex", alignItems: "center", mb: 1.5 }}
            >
              <CloudQueueIcon sx={{ color: "secondary.main", mr: 1.5 }} />
              <Typography variant="body1">{feature}</Typography>
            </Box>
          ))}
          <Grid sx={{ mt: 3 }}>
            <Button href="/get-started" variant="contained" sx={{ mr: 2 }}>
              Call to Action
            </Button>
          </Grid>
        </Grid>
        <Grid
          item
          xs={12}
          md={6}
          width={{ lg: "30%" }}
          sx={{ textAlign: "center" }}
        >
          <img
            src={data.image}
            alt={data.heading}
            style={{ maxWidth: "100%", height: "auto", maxHeight: 450 }}
          />
        </Grid>
      </Grid>
    </Container>
  </Box>
);

export default function Financial() {
  const location = useLocation();

  useEffect(() => {
    const path = location.pathname.replace("/industry/", "");

    const section = document.getElementById(path);
    if (section) {
      section.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <Solutions />
      {Object.entries(financialSolutions).map(([key, data], index) => (
        <FinancialSolutionSection
          key={key}
          id={key}
          data={data}
          index={index}
        />
      ))}
      <BlogCards />
      <Footer />
    </ThemeProvider>
  );
}
